/**
 * Client for the Team half of the `workspace` edge function: a workspace's
 * members, its invitations, and the invite page's accept. No component talks to
 * `workspace_members` or `workspace_invitations` directly: who may read or
 * change the team is the server's call (owner or admin; a role change is the
 * owner's alone), and its refusals arrive as `ApiError`s whose message is the
 * sentence to show. Field names are the column names. Keep in sync with
 * `supabase/functions/workspace/` (`member.*`, `invitation.*`).
 */

import { ApiError, api } from "@/lib/api-client";

const BASE = "/workspace";

/** What a member can be given, or invited as. Never "owner": the owner is the workspace's `owner_id`. */
export type MemberRole = "admin" | "developer" | "member";

/** A member as the Team tab lists them. `email` is null when the account has none, or it could not be looked up. */
export interface TeamMember {
  id: string;
  user_id: string;
  role: string;
  created_at: string | null;
  email: string | null;
}

/** The roster. The owner is not among `members` (their own row is left out), so it is named on its own. */
export interface Team {
  owner: { user_id: string; email: string | null };
  members: TeamMember[];
}

/** A pending invitation. Never its token: whoever holds that can accept it. */
export interface Invitation {
  id: string;
  email: string;
  role: string;
  expires_at: string;
  created_at: string | null;
}

/** Where an accepted invitation put the caller, and as what. */
export interface JoinedWorkspace {
  workspace_id: string;
  workspace_name: string;
  role: string;
}

/** The members, newest first, and the owner. */
export const getTeam = (workspaceId: string) => api.getItem<Team>(`${BASE}/${workspaceId}/members`);

/** The owner's alone; the owner's own role is refused with 403. */
export const changeMemberRole = (workspaceId: string, memberId: string, role: MemberRole) =>
  api.put<TeamMember>(`${BASE}/${workspaceId}/members/${memberId}`, { role });

/** The owner cannot be removed (403). */
export const removeMember = (workspaceId: string, memberId: string) =>
  api.del(`${BASE}/${workspaceId}/members/${memberId}`);

/** The pending ones, newest first, in one list. */
export const listInvitations = (workspaceId: string) =>
  api.getList<Invitation>(`${BASE}/${workspaceId}/invitations`);

/**
 * Sends the email too. Refused with 409 for someone already a member or already
 * invited, 429 past the day's limit, 422 for an address that is not one.
 */
export const sendInvitation = (workspaceId: string, email: string, role: MemberRole) =>
  api.post<Invitation>(`${BASE}/${workspaceId}/invitations`, { email, role });

export const revokeInvitation = (workspaceId: string, invitationId: string) =>
  api.del(`${BASE}/${workspaceId}/invitations/${invitationId}`);

/** No workspace id: the token names it. A wrong account is a 403 — see `invitedEmailOf`. */
export const acceptInvitation = (token: string) => api.post<JoinedWorkspace>(`${BASE}/invitations/accept`, { token });

/**
 * The address an invitation was sent to, when `error` is the accept's refusal of
 * a signed-in account with another one (403, `data.invited_email`); else null.
 */
export function invitedEmailOf(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 403) return null;
  const invited = (error.data as { invited_email?: unknown } | undefined)?.invited_email;
  return typeof invited === "string" ? invited : null;
}
